import { useState } from 'react'
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query'
import { patientService } from '../services/patientService'
import { userService } from '../services/userService'
import { PatientFormData } from '../types/patient'
import { formatUserName } from '../utils/name'
import './QuickAddPatient.css'

interface QuickAddPatientProps {
  onPatientCreated: (patientId: string) => void
  onCancel: () => void
}

export function QuickAddPatient({ onPatientCreated, onCancel }: QuickAddPatientProps) {
  const queryClient = useQueryClient()
  const [formData, setFormData] = useState<Partial<PatientFormData>>({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: undefined,
    phone: '',
    email: '',
    street: '',
    city: '',
    registeredGpName: '',
    registeredGpId: '',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  const { data: clinicalStaff, isLoading: staffLoading } = useQuery({
    queryKey: ['clinical-staff'],
    queryFn: () => userService.getClinicalStaff(),
  })

  const createMutation = useMutation({
    mutationFn: (data: PatientFormData) => patientService.create(data),
    onSuccess: (newPatient) => {
      queryClient.invalidateQueries({ queryKey: ['patients'] })
      // Call callback immediately - the mutation has completed successfully
      onPatientCreated(newPatient.id)
    },
    onError: (error: any) => {
      alert(error?.message || 'Failed to create patient. Please try again.')
    },
  })

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.firstName || formData.firstName.trim().length < 2) {
      newErrors.firstName = 'First name is required (minimum 2 characters)'
    }
    if (!formData.lastName || formData.lastName.trim().length < 2) {
      newErrors.lastName = 'Last name is required (minimum 2 characters)'
    }
    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = 'Date of birth is required'
    } else {
      const dob = new Date(formData.dateOfBirth)
      const today = new Date()
      if (dob > today) {
        newErrors.dateOfBirth = 'Date of birth cannot be in the future'
      }
    }
    if (!formData.gender) {
      newErrors.gender = 'Gender is required'
    }
    if (!formData.phone || formData.phone.trim().length === 0) {
      newErrors.phone = 'Phone number is required'
    }
    if (formData.email && formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    e.stopPropagation()
    
    // Prevent any parent form submission
    if (e.nativeEvent) {
      e.nativeEvent.stopImmediatePropagation()
    }
    
    if (!validate()) {
      return
    }

    const patientData: PatientFormData = {
      firstName: formData.firstName!.trim(),
      lastName: formData.lastName!.trim(),
      dateOfBirth: formData.dateOfBirth!,
      gender: formData.gender!,
      phone: formData.phone!.trim(),
      email: formData.email?.trim() || undefined,
      street: formData.street!.trim(),
      city: formData.city!.trim(),
      registeredGpName: formData.registeredGpName!.trim(),
      ...(formData.registeredGpId && { registeredGpId: formData.registeredGpId }),
    }

    try {
      await createMutation.mutateAsync(patientData)
    } catch (error) {
      // Error handled in mutation onError
    }
  }

  const handleChange = (field: keyof PatientFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  return (
    <div className="quick-add-patient">
      <div className="quick-add-patient-header">
        <h3>Quick Add Patient</h3>
        <button
          type="button"
          className="quick-add-close"
          onClick={onCancel}
          aria-label="Close"
        >
          ×
        </button>
      </div>

      <div 
        className="quick-add-patient-form" 
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="quick-add-form-row">
          <div className="quick-add-form-group">
            <label htmlFor="firstName">
              First Name <span className="required">*</span>
            </label>
            <input
              id="firstName"
              type="text"
              value={formData.firstName || ''}
              onChange={(e) => handleChange('firstName', e.target.value)}
              required
              autoFocus
            />
            {errors.firstName && <span className="error-message">{errors.firstName}</span>}
          </div>

          <div className="quick-add-form-group">
            <label htmlFor="lastName">
              Last Name <span className="required">*</span>
            </label>
            <input
              id="lastName"
              type="text"
              value={formData.lastName || ''}
              onChange={(e) => handleChange('lastName', e.target.value)}
              required
            />
            {errors.lastName && <span className="error-message">{errors.lastName}</span>}
          </div>
        </div>

        <div className="quick-add-form-row">
          <div className="quick-add-form-group">
            <label htmlFor="dateOfBirth">
              Date of Birth <span className="required">*</span>
            </label>
            <input
              id="dateOfBirth"
              type="date"
              value={formData.dateOfBirth || ''}
              onChange={(e) => handleChange('dateOfBirth', e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              required
            />
            {errors.dateOfBirth && <span className="error-message">{errors.dateOfBirth}</span>}
          </div>

          <div className="quick-add-form-group">
            <label htmlFor="gender">
              Gender <span className="required">*</span>
            </label>
            <select
              id="gender"
              value={formData.gender || ''}
              onChange={(e) => handleChange('gender', e.target.value)}
              required
            >
              <option value="">Select...</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
              <option value="unknown">Unknown</option>
            </select>
            {errors.gender && <span className="error-message">{errors.gender}</span>}
          </div>
        </div>

        <div className="quick-add-form-row">
          <div className="quick-add-form-group">
            <label htmlFor="phone">
              Phone <span className="required">*</span>
            </label>
            <input
              id="phone"
              type="tel"
              value={formData.phone || ''}
              onChange={(e) => handleChange('phone', e.target.value)}
              placeholder="085-123-4567"
              required
            />
            {errors.phone && <span className="error-message">{errors.phone}</span>}
          </div>

          <div className="quick-add-form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={formData.email || ''}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="patient@example.com"
            />
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>
        </div>

        <div className="quick-add-form-row">
          <div className="quick-add-form-group">
            <label htmlFor="street">
              Street Address <span className="required">*</span>
            </label>
            <input
              id="street"
              type="text"
              value={formData.street || ''}
              onChange={(e) => handleChange('street', e.target.value)}
              placeholder="Street address"
              required
            />
            {errors.street && <span className="error-message">{errors.street}</span>}
          </div>

          <div className="quick-add-form-group">
            <label htmlFor="city">
              City <span className="required">*</span>
            </label>
            <input
              id="city"
              type="text"
              value={formData.city || ''}
              onChange={(e) => handleChange('city', e.target.value)}
              placeholder="City"
              required
            />
            {errors.city && <span className="error-message">{errors.city}</span>}
          </div>
        </div>

        <div className="quick-add-form-row">
          <div className="quick-add-form-group">
            <label htmlFor="registeredGpName">
              Registered GP <span className="required">*</span>
            </label>
            <select
              id="registeredGpName"
              value={formData.registeredGpId || ''}
              onChange={(e) => {
                const id = e.target.value
                handleChange('registeredGpId', id)
                const staff = clinicalStaff?.find((s) => s.id === id)
                handleChange('registeredGpName', staff ? formatUserName(staff as any) : '')
              }}
              required
              disabled={staffLoading}
            >
              <option value="">Select practitioner...</option>
              {clinicalStaff?.map((staff) => (
                <option key={staff.id} value={staff.id}>
                  {formatUserName(staff as any)} ({staff.role})
                </option>
              ))}
            </select>
            {errors.registeredGpName && <span className="error-message">{errors.registeredGpName}</span>}
          </div>
        </div>

        <div className="quick-add-form-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={onCancel}
            disabled={createMutation.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={createMutation.isPending}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              if (e.nativeEvent && e.nativeEvent.stopImmediatePropagation) {
                e.nativeEvent.stopImmediatePropagation()
              }
              handleSubmit(e)
            }}
          >
            {createMutation.isPending ? 'Creating...' : 'Create & Select Patient'}
          </button>
        </div>
      </div>
    </div>
  )
}
